/**
 * Плашка Р-2 «Культур хватает для работы» (AK-546, ч.2; wireframe сост. 1).
 *
 * Решение founder Р-2 (05.08): **состояние справочника объявляет человек**.
 * Система его не вычисляет и процентов готовности не рисует — поэтому здесь
 * запись «кто и когда объявил», а не расчёт по счётчикам.
 *
 * Решение founder Д (07.08): агроному плашку **не показываем** — «это просто
 * мнение админа в процессе использования». Гейт — право редактировать
 * справочник (`company_reference:u`), а не право разбирать порции: разбирать
 * может и агроном, а достаточность оценивает тот, кто справочник ведёт.
 */
import { Check, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { formatRuDate } from '@/lib/dateFmt';
import {
  useDeclaration,
  useSetDeclaration,
  type ReviewEntityType,
} from '@/lib/reference/catalogReview';

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

const NOUN: Record<ReviewEntityType, string> = {
  crop: 'Культур',
  operation_type: 'Названий тех.операций',
};

export function SufficiencyCard({
  entityType,
  companyId,
  canEdit,
}: {
  entityType: ReviewEntityType;
  companyId: string | null;
  /** Право редактора справочника. Без него плашки нет вовсе (решение Д). */
  canEdit: boolean;
}) {
  const declaration = useDeclaration(entityType, companyId, canEdit);
  const setDeclaration = useSetDeclaration(entityType);

  if (!canEdit || !companyId) return null;

  const state = declaration.data?.state ?? null;
  const enough = state === 'sufficient';
  const next = enough ? 'needs_more' : 'sufficient';

  function handleToggle() {
    if (!companyId) return;
    setDeclaration.mutate(
      { companyId, state: next },
      {
        onSuccess: () =>
          toast.success(
            next === 'sufficient'
              ? 'Отмечено: для работы хватает'
              : 'Отмечено: справочник нужно дополнить',
          ),
        onError: (e) => toast.error(errMsg(e, 'Не удалось сохранить отметку')),
      },
    );
  }

  return (
    <Card
      className={cn(
        'flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between',
        enough ? 'border-primary/40 bg-primary/10' : 'border-accent-brand/40 bg-accent-brand/10',
      )}
    >
      <div className="flex items-start gap-2">
        {enough ? (
          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
        ) : (
          <TriangleAlert className="mt-1 h-4 w-4 shrink-0 text-accent-brand" />
        )}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {state === null
                ? `${NOUN[entityType]} хватает для работы?`
                : enough
                  ? `${NOUN[entityType]} хватает для работы`
                  : 'Справочник нужно дополнить'}
            </span>
            <Badge variant="secondary">видно только вам</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {state === null
              ? 'Это ваша оценка, а не расчёт системы: отметьте, когда решите, что для работы всего достаточно.'
              : `Отметил${declaration.data?.declared_at ? ` ${formatRuDate(declaration.data.declared_at)}` : ''}. Это оценка человека, агроному она не показывается.`}
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        disabled={setDeclaration.isPending}
        onClick={handleToggle}
      >
        {state === null
          ? 'Отметить «хватает»'
          : enough
            ? 'Изменить: «нужно дополнить»'
            : 'Изменить: «хватает»'}
      </Button>
    </Card>
  );
}

export default SufficiencyCard;
